import { formatCnyCents, formatUsdMicros } from './formatters.ts';
import { cx } from '../ui/cx.ts';

/**
 * Shared money cell used by 客户账单 (USD + CNY) and credit surfaces
 * (CNY only, router#2085). One implementation so magnitude / signed /
 * null formatting cannot drift.
 *
 * `signed` has no default: unsigned prints magnitude. A book balance that
 * can go negative must not use this cell unsigned (#2079).
 *
 * `display: 'cny'` is a first-class mode — it does not take a USD amount
 * at all. Do not pass `usdMicros={null}` to hide the dollar line.
 */

interface DualCurrencyAmountShared {
  readonly cnyCents: number | null;
  readonly signed: boolean;
  readonly align?: 'start' | 'end';
  readonly usdTestId?: string;
  readonly cnyTestId?: string;
}

export type DualCurrencyAmountProps =
  | (DualCurrencyAmountShared & {
      readonly display?: 'dual';
      readonly usdMicros: number | null;
    })
  | (DualCurrencyAmountShared & {
      readonly display: 'cny';
    });

export function DualCurrencyAmount(props: DualCurrencyAmountProps): JSX.Element {
  const { cnyCents, signed, align = 'end', usdTestId, cnyTestId } = props;
  const cnyLine = cnyCents === null ? null : formatCnyCents(cnyCents, signed);
  if (props.display === 'cny') {
    return (
      <div className={cx('flex flex-col', align === 'end' ? 'items-end' : 'items-start')}>
        <span data-testid={cnyTestId}>{cnyLine ?? '—'}</span>
      </div>
    );
  }
  const usdLine = props.usdMicros === null ? null : formatUsdMicros(props.usdMicros, signed);
  const stacked = usdLine !== null && cnyLine !== null;
  return (
    <div className={cx('flex flex-col', align === 'end' ? 'items-end' : 'items-start')}>
      <span data-testid={usdLine !== null ? usdTestId : cnyTestId}>
        {usdLine ?? cnyLine ?? '—'}
      </span>
      {stacked ? (
        <span className="text-caption text-paper-dim" data-testid={cnyTestId}>
          {cnyLine}
        </span>
      ) : null}
    </div>
  );
}
